/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import {
  toWidget,
  toWidgetEditable,
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { INSERT_INFO_PANEL_COMMAND } from './constants';
import { InsertInfoPanelCommand } from './info-panel-command';
import styles from './info-panel.module.css';

export interface InfoPanelPluginConfiguration {
  iconRenderer: (paneltype: string, container: HTMLElement) => void;
}

export class InfoPanelEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'InfoPanelEditing';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_INFO_PANEL_COMMAND,
      new InsertInfoPanelCommand(this.editor),
    );
  }

  defineSchema() {
    // ADDED
    const { schema } = this.editor.model;

    schema.register('infoPanel', {
      // Behaves like a self-contained object (e.g. an image).
      isObject: true,

      // Allow in places where other blocks are allowed (e.g. directly in the root).
      allowWhere: '$block',
      allowAttributes: 'paneltype',
    });
    schema.register('infoPanelDescription', {
      // Cannot be split or left by the caret.
      isLimit: true,
      allowIn: 'infoPanel',

      // Allow content which is allowed in the root (e.g. paragraphs).
      allowContentOf: '$root',
    });
  }

  defineConverters() {
    const { conversion } = this.editor;
    const renderPanelIcon =
      this.editor.config.get<InfoPanelPluginConfiguration>(
        'infopanel',
      ).iconRenderer;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'section',
        classes: 'info-panel',
      },
      model: 'infoPanel',
    });
    conversion.for('dataDowncast').elementToElement({
      model: 'infoPanel',
      view: {
        name: 'section',
        classes: 'info-panel',
      },
    });
    conversion.attributeToAttribute({
      model: 'paneltype',
      view: 'data-paneltype',
    });
    conversion.for('editingDowncast').elementToElement({
      model: 'infoPanel',
      view: (modelElement, { writer: viewWriter }) => {
        const paneltype = modelElement.getAttribute('paneltype');

        const reactWrapper = viewWriter.createUIElement(
          'span',
          {
            class: styles['document__info-panel__icon'],
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderPanelIcon(paneltype, domElement);

            return domElement;
          },
        );

        const section = viewWriter.createContainerElement(
          'section',
          {
            class: styles['ck-rl-info-panel'],
            'data-paneltype': paneltype,
          },
          [reactWrapper],
        );

        return toWidget(section, viewWriter, { label: 'info panel widget' });
      },
    });
    conversion.for('upcast').elementToElement({
      model: 'infoPanelDescription',
      view: {
        name: 'div',
        classes: 'info-panel-description',
      },
    });
    conversion.for('dataDowncast').elementToElement({
      model: 'infoPanelDescription',
      view: {
        name: 'div',
        classes: 'info-panel-description',
      },
    });
    conversion.for('editingDowncast').elementToElement({
      model: 'infoPanelDescription',
      view: (modelElement, { writer: viewWriter }) => {
        // Note: You use a more specialized createEditableElement() method here.
        const div = viewWriter.createEditableElement('div', {
          class: styles['ck-rl-info-panel-description'],
        });

        return toWidgetEditable(div, viewWriter);
      },
    });
  }
}
